const PGAdapter = require('./../../common/pg-adapter')
const { getOrderByClause, arrayOrStringPreparer } = require('../../tools/sql-tools')

const {
  SHARED_ROUTE_COLUMNS,
  SHARED_VIRTUAL_COLUMNS,
  SHARED_GET_RELATED,
  SHARED_COLUMN_PREPARERS,
  arrayPreparer
} = require('./route-pg-adapter-shared')

const ROUTE_VIEW_TABLE_NAME = 'avocado.vw_route_with_program'

class RouteViewPGDataAdapter extends PGAdapter {
  constructor (pgConnection, username, logger) {
    const columnPreparers = {
      ...SHARED_COLUMN_PREPARERS,
      status: arrayOrStringPreparer,
      program_ids: arrayPreparer,
      state: (key, value) => {
        const states = Array.isArray(value) ? value : [value]
        if (states.length === 1) {
          // Single state - use simple ILIKE
          return [{
            value: states[0],
            statement: `
                EXISTS (
                  SELECT 1 FROM avocado.data_shipment shipments
                  WHERE
                    shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                    shipments.destination_id ILIKE '%:state:'||%INDEX%||':%'
                )
              `
          }]
        }
        // Multiple states - use ILIKE ANY with array of patterns
        return [{
          value: states.map(s => '%:state:' + s + ':%'),
          statement: `
              EXISTS (
                SELECT 1 FROM avocado.data_shipment shipments
                WHERE
                  shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                  shipments.destination_id ILIKE ANY(%INDEX%::TEXT[])
              )
            `
        }]
      },
      origin_id: (key, value) => [
        {
          value,
          statement: `
              EXISTS (
                SELECT 1 FROM avocado.data_shipment shipments
                WHERE 
                  shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                  shipments.origin_id = %INDEX%
              )
            `
        }
      ]
    }

    super(
      pgConnection,
      ROUTE_VIEW_TABLE_NAME,
      username,
      [...SHARED_ROUTE_COLUMNS, 'program_ids', 'origin_id', 'state'],
      undefined,
      columnPreparers,
      logger,
      { getRelated: SHARED_GET_RELATED, virtualColumns: SHARED_VIRTUAL_COLUMNS }
    )
  }

  addRelatedCarrierColumn () {
    return `
      (
        SELECT row_to_json(org) FROM avocado.data_organisation org
        WHERE org.id = ${this.tableName}.carrier_id
        LIMIT 1
      ) AS carrier
    `
  }

  addRelatedShipmentsColumn () {
    return `
      COALESCE(
        (
          SELECT json_agg(DISTINCT s.*)
          FROM avocado.data_shipment s
          WHERE s.route_id = ${this.tableName}.couchdb_id
        ),
        '[]'
      ) AS shipments
    `
  }

  async getList ({
    ordering = this.idColumn,
    filter = {},
    limit = 50,
    offset = 0,
    tableExpression = null,
    useTableAsExpression = false,
    search = null,
    skipCount = false,
    skipData = false,
    getRelated
  } = {}) {
    const status = filter.status || []
    let customPreparer = {}
    const excludePreparers = []
    // Cancelled routes do not have any shipments linked to them hence the route filter is not included
    if (status === 'cancelled' || status.includes('cancelled')) {
      // Cancelled routes do not have a program id linked to them based on avocado.vw_route_with_program view definition
      // filtering by program ids won't return results so we remove them to get cancelled routes returned
      excludePreparers.push('program_ids')
      delete filter.program_ids
      if (filter.program_ids) {
        filter.program_ids.push(null)
      }
      customPreparer = {
        state: (key, value) => {
          const states = Array.isArray(value) ? value : [value]
          if (states.length === 1) {
            return [{
              value: states[0],
              statement: `
                  EXISTS (
                    SELECT 1 FROM avocado.data_shipment shipments
                    WHERE
                      shipments.destination_id ILIKE '%:state:'||%INDEX%||':%'
                  )
                `
            }]
          }
          return [{
            value: states.map(s => '%:state:' + s + ':%'),
            statement: `
                EXISTS (
                  SELECT 1 FROM avocado.data_shipment shipments
                  WHERE
                    shipments.destination_id ILIKE ANY(%INDEX%::TEXT[])
                )
              `
          }]
        }
      }
    }
    const {whereText, whereValues} = this.getWhereCondition(filter, search, customPreparer, excludePreparers)
    const resultList = {}

    if (!skipData) {
      const sortText = getOrderByClause(
        ordering,
        this.columns.concat(this.virtualColumns),
        (tableExpression && useTableAsExpression ? this.tableName : undefined)
      )
      /*
      Postgresql usage reminder ():
      > ASC order is the default. Ascending order puts smaller values first,
      where “smaller” is defined in terms of the < operator.
      - https://www.postgresql.org/docs/13/queries-order.html
    */
      const values = whereValues.concat([limit, offset])

      const text = `
        SELECT 
          *,
          ${this.addRelatedCarrierColumn()},
          ${this.addRelatedShipmentsColumn()} 
        FROM ${tableExpression || this.tableName}
        ${whereText}
        ${sortText} 
        LIMIT $${values.length - 1} OFFSET $${values.length};`

      let { rows: results } = await this.pgConnection.query({ text, values })

      resultList.results = results
    }

    if (!skipCount) { // Sometimes we don't need it so we may skip it for performance reasons
      resultList.count = await this.getCount(undefined, whereText, whereValues, tableExpression, useTableAsExpression)
    }

    return resultList
  }
}

module.exports = RouteViewPGDataAdapter
